import { getCache, setCache } from "./cache";
import { getConversationIdCookie, getCookie } from "utils/cookie";
import {
	loadBrandParam,
	loadLocaleParam,
	validateAndAddStoreParam,
	validateParam,
} from "utils/paramsValidation";
import { getEnvironment } from "utils";
import appConfig from "config.json";
import { isEnUs } from "./localeValidation";

const { brands, defaultBaseApiUrl } = appConfig;
const userTypeKey = "userType";
const brand = loadBrandParam();
const appEnv = getEnvironment();
const conversationId = getConversationIdCookie();
const locale = loadLocaleParam();

export const apiUrl = (() => {
	// This validation can be removed once GIT-59084 is complete and tested
	if (
		brands[brand]?.optimizedApiEnabled &&
		brands[brand]?.domainsWithSupportedApi?.includes(window?.location.host)
	) {
		// We need to avoid adding locale when en-us is selected, as it gets removed
		// from akamai
		if (isEnUs(locale) || brands[brand]?.preventLocaleInApiPath)
			return `${window?.location.origin}${brands[brand]?.baseApiPath}`;

		return `${window?.location.origin}/${locale}${brands[brand]?.baseApiPath}`;
	}

	// GIT-59084: Keep the prefix logic, and remove/adapt previous validation once
	// new domain is working as expected
	let serviceEnv = "";
	switch (appEnv) {
		case "development":
		case "latest":
			serviceEnv = "latest.";
			break;
		case "stage":
			serviceEnv = "stage.";
			break;
		case "lt01":
			serviceEnv = brands[brand].useLt01ApiPrefix ? "lt01." : "load.";
			break;
		default:
			serviceEnv = "";
	}
	return (brands[brand]?.baseApiUrl || defaultBaseApiUrl).replace(
		"{env}",
		serviceEnv
	);
})();

const urlInterceptor = (url, locale, options = {}) => {
	if (url?.indexOf("/navigation/") === 0) {
		url = new URL(`${apiUrl}${url.replace("/navigation", "")}`);
		url.searchParams.append("brand", brand);
		url.searchParams.append("locale", locale);
		validateAndAddStoreParam(url);
		if (!options.headers) {
			options.headers = {};
		}
		options.headers["conversation-id"] = conversationId;
		if (brands[brand].usePaCookie && url.pathname.indexOf("/profile")) {
			const paCookie = getCookie("__pa");
			if (paCookie) {
				options.headers["__pa"] = paCookie;
			}
		}
		if (url.searchParams.has("travelAgentHeader")) {
			options.headers["travel-agent-header"] =
				url.searchParams.get("travelAgentHeader");
			url.searchParams.delete("travelAgentHeader");
		}
		if (
			url.pathname.indexOf("/content/") &&
			!url.searchParams.has(userTypeKey)
		) {
			url.searchParams.append(
				userTypeKey,
				url.searchParams.has("store") ? "TRAVEL_AGENT" : "GUEST"
			);
		}
		if (brands[brand].filterContentKeys) {
			url.searchParams.delete("userType");
		}
		if (
			url.pathname.indexOf("/content/") !== -1 &&
			validateParam("customLayout")
		) {
			url.searchParams.append("customLayout", "true");
		}
		url = url.href;
	}
	return { url, options };
};

export const customFetch = async (url, options, cacheValidMinutes, signal) => {
	const { url: requestUrl, options: requestOptions } = urlInterceptor(
		url,
		locale,
		options || {}
	);
	let res;
	if (cacheValidMinutes) {
		res = await getCache(requestUrl);
	}
	if (!res) {
		let options = {
			...requestOptions,
		};
		if (signal) {
			options = {
				...requestOptions,
				...{ signal },
			};
		}
		res = await fetch(requestUrl, options);
		if (!res.ok) {
			// eslint-disable-next-line no-throw-literal
			throw {
				body: await res.json(),
				message: res.message || "Request error",
				status: res.status,
				statusText: res.statusText,
				type: res.type,
			};
		}
		if (cacheValidMinutes) {
			await setCache(requestUrl, res.clone(), cacheValidMinutes);
		}
	}
	let json = {};
	if (res.status !== 204) {
		json = await res.json();
	}
	return json;
};
